/**
 * Formats a property number for an input field.
 *
 * The engine reports float32 widened to double, so `0.1` arrives as
 * `0.10000000149011612`; the shortest decimal with the same float32 is shown.
 * Integers are kept verbatim so a large id is not rounded.
 *
 * @param value - Number as the registry holds it.
 * @returns The text to show; empty for a non-finite value.
 */
export const formatPropertyNumber = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '';
  }

  if (Number.isInteger(value)) {
    return String(value);
  }

  const target = Math.fround(value);

  for (let digits = 7; digits <= 9; digits += 1) {
    const text = String(Number(value.toPrecision(digits)));

    if (Math.fround(Number(text)) === target) {
      return text;
    }
  }

  return String(value);
};

/**
 * Tells whether two numbers are the same property value.
 *
 * Compared at float32 the engine's widening disappears while every edit it
 * could tell apart still counts; integers compare exactly, since an id must.
 *
 * @param a - One number.
 * @param b - The other.
 * @returns Whether the engine would hold them as the same value.
 */
export const propertyNumbersEqual = (a: number, b: number): boolean =>
  Number.isInteger(a) && Number.isInteger(b)
    ? a === b
    : Math.fround(a) === Math.fround(b);

/**
 * Tells whether two numbers read the same at a fixed number of decimals,
 * compared as numbers so `-0.00` equals `0.00`.
 *
 * @param a - One number.
 * @param b - The other.
 * @param decimals - Decimals the input shows.
 * @returns Whether they format to the same value.
 */
export const numbersEqualAtDecimals = (
  a: number,
  b: number,
  decimals: number,
): boolean => Number(a.toFixed(decimals)) === Number(b.toFixed(decimals));
